<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref, shallowRef } from 'vue'
import Faq from '../faq/Faq.vue'
import TicketComponent from '@/components/subscription/Ticket.vue'
import SubscriptionComponent from '@/components/subscription/Subscription.vue'
import { useCompatibilityStore } from '@/stores/compatibilityStore'
import Footer from '@/components/Footer.vue'
import { SubscriptionType } from '@/types'
import Testimonials from '@/components/subscription/Testimonials.vue'
import { useAuthStore } from '@/stores/authStore'

interface Props {

}

defineProps<Props>()

const { t } = useI18n()

const tickets = [
	{ ticket: 60, trial: true, best: false, crypto: false, bonus: 0, price: 2.99, g2a: 'https://www.g2a.com/faplab-60-tickets-faplab-key-global-i10000510342007' },
	{ ticket: 400, trial: false, best: false, crypto: false, bonus: 0, price: 8.99, g2a: 'https://www.g2a.com/faplab-400-tickets-faplab-key-global-i10000510342001' },
	{ ticket: 1000, trial: false, best: false, crypto: false, bonus: 171, price: 18.99, g2a: 'https://www.g2a.com/faplab-1000-tickets-faplab-key-global-i10000510342002' },
	{ ticket: 2500, trial: false, best: true, crypto: false, bonus: 968, price: 34.99, g2a: 'https://www.g2a.com/faplab-1000-tickets-faplab-key-global-i10000510342003' },
	{ ticket: 6600, trial: false, best: false, crypto: false, bonus: 3752, price: 63.99, g2a: 'https://www.g2a.com/faplab-1000-tickets-faplab-key-global-i10000510342004' },
	{ ticket: 14000, trial: false, best: false, crypto: false, bonus: 10284, price: 82.99, g2a: 'https://www.g2a.com/faplab-1000-tickets-faplab-key-global-i10000510342006' },
	/* { ticket: 100000, best: false, crypto: true, bonus: 33333, price: 914.99, g2a: 'https://www.g2a.com/faplab-1000-tickets-faplab-key-global-i10000510342005', limited: 0 }, */
]

const subs = computed(() => {
	const res = [
		{ month: 1, type: SubscriptionType.Pro, ticket: 100, crypto: true, price: { usd: 24.99, eur: 24.99 }, energy: 1200, imagePerDay: 600, basePrice: 30.99, g2a: 'https://www.g2a.com/faplab-subscription-pro-1-month-faplab-key-global-i10000510777001' },
		{ month: 1, type: SubscriptionType.Basic, ticket: 1, crypto: true, price: { usd: 11.99, eur: 11.99 }, energy: 350, imagePerDay: 175, basePrice: 12.49, g2a: 'https://www.g2a.com/faplab-subscription-basic-1-month-faplab-key-global-i10000510777005' },
	]
	if (useAuthStore().user.plan === SubscriptionType.Free) {
		res.unshift({ month: 1, type: SubscriptionType.Free, ticket: 0, crypto: false, price: { usd: 0, eur: 0 }, energy: 0, imagePerDay: 5, basePrice: 0, g2a: '' })
	}
	return res
})

const visibleTickets = ref<number>(0)
const visibleSubs = ref<number>(0)
onMounted(() => {
	const delay = useCompatibilityStore().mobileDevice ? 0 : 120
	setTimeout(() => {
		tickets.forEach((_, i) => {
			setTimeout(() => visibleTickets.value = i + 1, i * delay)
		})
		subs.value.forEach((_, i) => {
			setTimeout(() => visibleSubs.value = i + 1, i * delay)
		})
	}, 100)
})

const moved = ref(false)
const usd = shallowRef(true)
/*
fetch('https://ipapi.co/json/')
	.then(res => res.json())
	.then((data) => {
		usd.value = !data.in_eu
	})
	.catch(() => {
		usd.value = true
	}) */
</script>

<template>
	<div class="flex flex-col gap-8 items-center flex-nowrap  menu:mt-[70px] mt-[120px] relative px-4 w-full sm:w-10/12 md:w-10/12 mx-auto">
		<div class="gap-2 flex-center flex-col mt-[20px]">
			<div
				class="bg-slate-800 rounded-full flex justify-between gap-8 px-3 py-4 cursor-pointer relative group select-none mb-4"
				@click="moved = !moved"
			>
				<div
					class="absolute h-full w-6/12 bg-gradient-to-br from-slate-600 via-purple-600/60 to-slate-700 top-0 rounded-full z-[1] left-0 transition-transform duration-300"
					:class="[
						moved ? 'translate-x-full' : 'translate-x-0',
					]"
				/>
				<span class="z-[2] w-[100px] flex-center text-sm font-bold">{{ t('common.upgrade') }}</span>
				<span class="z-[2] w-[100px] flex-center text-sm font-bold">{{ t('common.tickets') }}</span>
			</div>
			<h1 class="p-0 m-0 text-3xl md:text-4xl flex justify-center w-full font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-100 to-orange-600 pb-2 text-center">
				<template v-if="moved">
					{{ t('shop.title') }}
				</template>
				<template v-else>
					{{ t('shop.title_pro.0') }}<br>{{ t('shop.title_pro.1') }}
				</template>
			</h1>
			<h3 class="font-normal text-auto text-base p-0 m-0">
				<span
					v-if="moved"
					class="em text-slate-300 leading-relaxed text-center"
				>
					{{ t('shop.subtitle') }}
				</span>
				<span
					v-else
					class="em text-slate-300 leading-relaxed text-center"
				>
					{{ t('shop.subtitle_pro') }}
				</span>
			</h3>
		</div>

		<div class="flex flex-wrap gap-8 py-10 justify-center w-full">
			<template v-if="moved">
				<TicketComponent
					v-for="(ticket) in tickets.slice(0, visibleTickets)"
					:key="ticket.ticket"
					v-bind="ticket"
					:usd="usd"
				/>
			</template>
			<template v-else>
				<SubscriptionComponent
					v-for="(sub) in subs.slice(0, visibleSubs)"
					:key="sub.ticket"
					:usd="usd"
					v-bind="sub"
				/>
			</template>
		</div>

		<Testimonials class="mt-[100px]" />
		<Divider class="my-[100px] w-8/12 mx-auto" />
		<Faq />
		<Footer />
	</div>
</template>

<style lang="scss" scoped>

</style>
